// Task 9 (WU-2.4) — supervisor "Evict operator" dialog, launched from the
// staff-monitor LiveRoster row action. Structure mirrors ComposeNoteDialog
// (directed-notes/components/ComposeNoteDialog.tsx): portal-free backdrop +
// modal, window-level Escape listener (the T2/TeamDialog house idiom — a
// div-level onKeyDown goes deaf once focus lands outside any focusable
// descendant).
//
// The operator is always preset (unlike ComposeNoteDialog's optional
// picker) — this dialog only ever opens from a roster row that already
// knows who it's targeting. Step options come from the shared STEP_OPTIONS
// home (lib/step-options.ts); job types come from useJobTypes (the same
// tenant job-types fetch QueueBlocksListPage feeds into CreateBlockDialog),
// with UNCLASSIFIED_JOB_TYPE_ID defensively filtered out of the "Whole
// step" select — it's a synthetic lane id (queues/hooks/useQueues.ts), not
// a real JobType, so evicting "onto" it would be meaningless.
import { useEffect, useMemo, useState } from "react";
import { STEP_OPTIONS } from "@/lib/step-options";
import { useJobTypes } from "@/features/admin/hooks/useJobTypes";
import { UNCLASSIFIED_JOB_TYPE_ID } from "@/features/queues/hooks/useQueues";
import { ApiError } from "@/lib/api-client";
import { toast } from "@/components/Toaster";
import { getApiErrorCode, showApiError } from "@/lib/error-toast";
import { evictionsApi, type MyEviction } from "../api/client";
import styles from "../queue-evictions.module.css";

const REASON_MAX = 200;

type EvictionMode = MyEviction["mode"];

// Exact copy from the brief — surfaced verbatim in the radio labels so
// operators understand exactly when the eviction takes effect.
const MODE_OPTIONS: { value: EvictionMode; label: string }[] = [
  { value: "immediate", label: "Immediate — releases their current document (keyed work is saved)" },
  { value: "after_document", label: "After current document" },
  { value: "after_batch", label: "After current batch" },
];

export interface EvictOperatorDialogProps {
  operator: { id: string; name: string | null };
  onClose: () => void;
  onCreated: () => void;
}

export function EvictOperatorDialog({ operator, onClose, onCreated }: EvictOperatorDialogProps) {
  const { items: jobTypes } = useJobTypes();
  const [stepType, setStepType] = useState<string>(STEP_OPTIONS[0]);
  const [jobTypeId, setJobTypeId] = useState("");
  const [mode, setMode] = useState<EvictionMode | "">("");
  const [reason, setReason] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [busy, setBusy] = useState(false);

  // Defensive filter — UNCLASSIFIED_JOB_TYPE_ID is a synthetic lane id, not
  // a real JobType row, so it shouldn't normally appear in useJobTypes()'s
  // response; excluded here anyway so a "Whole step" eviction can't be
  // confused with an explicit (and meaningless) unclassified-lane eviction.
  const jobTypeOptions = useMemo(
    () => jobTypes.filter((jt) => jt.id !== UNCLASSIFIED_JOB_TYPE_ID),
    [jobTypes],
  );

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const trimmedReason = reason.trim();
  const reasonTooLong = trimmedReason.length > REASON_MAX;
  // stepType always holds a real STEP_OPTIONS value (initialized to the first
  // option; the select has no blank entry), so it needs no emptiness check.
  const canSubmit = !busy && mode !== "" && trimmedReason.length > 0 && !reasonTooLong;

  async function submit() {
    setBusy(true);
    try {
      await evictionsApi.create({
        userId: operator.id,
        stepType,
        ...(jobTypeId ? { jobTypeId } : {}),
        mode: mode as EvictionMode,
        reason: trimmedReason,
        ...(expiresAt ? { expiresAt: new Date(expiresAt).toISOString() } : {}),
      });
      onCreated();
    } catch (e) {
      // TeamDialog idiom — name the specific 409 by its code; everything
      // else (including any future, differently-coded 409) falls through to
      // the standard error-toast pipeline.
      if (e instanceof ApiError && e.status === 409 && getApiErrorCode(e) === "eviction_exists") {
        toast.error("This operator already has an active eviction for that step.");
      } else {
        showApiError(e);
      }
      setBusy(false);
    }
  }

  return (
    <div className={styles.modalBackdrop} role="dialog" aria-modal="true" aria-label="Evict operator">
      <div className={styles.modal}>
        <h2>Evict operator</h2>
        <p className={styles.subtitle}>{operator.name ?? operator.id}</p>

        <div className={styles.field}>
          <label htmlFor="evict-step">
            <span>Step</span>
          </label>
          <select
            id="evict-step"
            value={stepType}
            aria-label="step"
            onChange={(e) => setStepType(e.target.value)}
          >
            {STEP_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label htmlFor="evict-job-type">
            <span>Job type</span>
          </label>
          <select
            id="evict-job-type"
            value={jobTypeId}
            aria-label="job type"
            onChange={(e) => setJobTypeId(e.target.value)}
          >
            <option value="">Whole step</option>
            {jobTypeOptions.map((jt) => (
              <option key={jt.id} value={jt.id}>
                {jt.name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.radioGroup} role="radiogroup" aria-label="mode">
          {MODE_OPTIONS.map((opt) => (
            <label key={opt.value} className={styles.radioOption}>
              <input
                type="radio"
                name="evict-mode"
                value={opt.value}
                checked={mode === opt.value}
                onChange={() => setMode(opt.value)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>

        <div className={styles.field}>
          <label htmlFor="evict-reason">
            <span>Reason</span>
          </label>
          <textarea
            id="evict-reason"
            value={reason}
            aria-label="reason"
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this operator being moved off the queue?"
            rows={3}
          />
          {/* Counter measures the TRIMMED length — the same value the ≤200
              validation gates on and the POST sends — so the displayed
              number can never contradict the rule it advertises (review
              fix: 195 chars + padding previously read "201 / 200" with
              submit still enabled). */}
          <small className={styles.help}>
            {trimmedReason.length} / {REASON_MAX}
          </small>
          {reasonTooLong && (
            <span className={styles.fieldError}>Reason must be {REASON_MAX} characters or fewer.</span>
          )}
        </div>

        <div className={styles.field}>
          <label htmlFor="evict-until">
            <span>Until (optional)</span>
          </label>
          <input
            id="evict-until"
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
          <small className={styles.help}>Leave blank for no automatic expiry.</small>
        </div>

        <div className={styles.modalActions}>
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.primary}
            disabled={!canSubmit}
            onClick={() => void submit()}
          >
            Evict…
          </button>
        </div>
      </div>
    </div>
  );
}
